// @flow
import type {
    TIsVerifyRequestBody,
    TAuthData,
    TSignUpRequestBody,
    TVerificationRequestBody,
    TSuccessResponse,
    TForgotPasswordRequestBody,
    TEmailIsVerifyRequestBody,
    TEmailVerificationRequestBody,
} from 'modules/auth/types/user';
import type {HttpService} from 'modules/common/services/http';
import type {TResponse} from 'types';

/**
 * Сервис для запросов модуля auth
 */
export class AuthApi {
    /**
     * Сервис для отправки HTTP запросов
     */
    _http: HttpService;

    /**
     * @param http Сервис для отправки HTTP запросов
     */
    constructor(http: HttpService) {
        this._http = http;
    }

    /**
     * Регистрация компании
     * @param values значения из формы
     * @returns данные по регистрации
     */
    async signUp<T>(values: TSignUpRequestBody): TResponse<T> {
        return this._http.post<T>('/company/registration', {
            body: values,
            extraOptions: {
                dataType: 'FORM_DATA',
            },
            options: {
                headers: {
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Аутентификация пользователя
     * @param email почта
     * @param password пароль
     * @param activeLanguageId id языка
     * @return данные аутентифицированного пользователя
     */
    async signIn<T>({email, password, activeLanguageId}: TAuthData): TResponse<T> {
        return this._http.post<T>('/login', {
            body: {
                activeLanguageId: Number(activeLanguageId),
            },
            options: {
                headers: {
                    Authorization: `Basic ${window.btoa(unescape(encodeURIComponent(email + ':' + password)))}`,
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * АвтоАутентификация пользователя
     * @return данные аутентифицированного пользователя
     */
    async autoSignIn<T>(): TResponse<T> {
        return this._http.post<T>('/login', {
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Проверка проходил ли пользователь уже верификацию
     * @param values хэш почты (находится в урле ссылки)
     * @returns данные по первому этапу верификации аккаунта
     */
    async isVerify(values: TIsVerifyRequestBody): TResponse<TSuccessResponse> {
        return this._http.post('/user/is-verify', {
            body: values,
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Верификация пользователя
     * @param values значения из формы
     * @returns данные по второму этапу верификации аккаунта
     */
    async verification(values: TVerificationRequestBody): TResponse<TSuccessResponse> {
        return this._http.post('/user/verification', {
            body: values,
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Проверка надобности в верификации почты
     * @param values хэш почты (находится в урле ссылки)
     * @returns данные по первому этапу верификации аккаунта
     */
    async emailIsVerify(values: TEmailIsVerifyRequestBody): TResponse<TSuccessResponse> {
        return this._http.post('/user/email-is-verify', {
            body: values,
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Верификации почты
     * @param values значения из формы
     * @returns данные по второму этапу верификации аккаунта
     */
    async emailVerification(values: TEmailVerificationRequestBody): TResponse<TSuccessResponse> {
        return this._http.post('/user/email-verification', {
            body: values,
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Запросить ссылку на изменение пароля (забыли пароль)"
     * @param values значения из формы
     * @return данные аутентифицированного пользователя
     */
    async forgotPassword<T>(values: TForgotPasswordRequestBody): TResponse<T> {
        return this._http.post<T>('/user/forgot-password', {
            body: values,
            extraOptions: {
                dataType: 'FORM_DATA',
            },
            options: {
                headers: {
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Проверка проходил ли пользователь по ссылке восстановления пароля"
     * @param values хэш почты (находится в урле ссылки)
     * @returns данные по первому этапу восстановления пароля
     */
    async isRecovery(values: TIsVerifyRequestBody): TResponse<TSuccessResponse> {
        return this._http.post('/user/is-recovery', {
            body: values,
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Восстановление пароля"
     * @param values значения из формы
     * @returns данные по второму этапу восстановления пароля
     */
    async recoveryPassword(values: TVerificationRequestBody): TResponse<TSuccessResponse> {
        return this._http.post('/user/recovery-password', {
            body: values,
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }
}
